import { normalizeAllDayOverlayBounds } from "./calendar-overlay-layout.js";
import { expandMarksToCalendarEvents } from "../../availability/utils/marks-render";

/**
 * Build the current user's self-availability overlay records (linked-calendar
 * conflicts/free-time, availability marks, and other finalized sessions they're
 * booked into) clipped to a time `window`, each tagged `kind` =
 * "conflict" | "preferred-align" | "feasible-align".
 *
 * Extracted from SchedulerPage so it can be driven by TWO windows: the calendar
 * grid's viewport window (perf-scoped) AND the List View's full slot-span window
 * (so a far-month conflict still badges its slot regardless of grid navigation —
 * the A-7 windowing fix). Pure + unit-tested.
 *
 * @param {object} opts
 * @param {string} opts.uid                        current user id (no records without it)
 * @param {{start: Date, end: Date}} opts.window    inclusive-ish [start, end) clamp
 * @param {string|null} [opts.currentSchedulerId]   the poll being voted on (its own finalized session is skipped)
 * @param {Array} [opts.availabilityEvents]         linked-calendar events ({startUtc,endUtc,calendarId,...})
 * @param {Array} [opts.availabilityMarks]          availability marks (RRULE-expandable)
 * @param {Array} [opts.finalizedSessions]          other finalized sessions ({schedulerId,startUtc,endUtc,title,hidden})
 * @param {Object} [opts.linkedCalendarsById]       calendarId → linkedCalendar doc
 * @returns {Array<{id,kind,title,startUtc,endUtc,allDay,...}>}
 */
export function buildOverlayRecords({
  uid,
  window,
  currentSchedulerId = null,
  availabilityEvents = [],
  availabilityMarks = [],
  finalizedSessions = [],
  linkedCalendarsById = {},
} = {}) {
  if (!uid || !window?.start || !window?.end) return [];
  const winStart = window.start.getTime();
  const winEnd = window.end.getTime();
  const out = [];

  const finalizeAllDayBounds = (record) => {
    if (!record.allDay) return record;
    const norm = normalizeAllDayOverlayBounds(record);
    if (!norm) return null;
    return { ...record, startUtc: norm.startUtc, endUtc: norm.endUtc };
  };

  // 1. Linked-calendar events.
  for (const evt of availabilityEvents || []) {
    const start = evt.startUtc ? new Date(evt.startUtc) : null;
    const end = evt.endUtc ? new Date(evt.endUtc) : null;
    if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) continue;
    if (end.getTime() <= winStart || start.getTime() >= winEnd) continue;
    const link = linkedCalendarsById[evt.calendarId] || null;
    let kind = null;
    if (link?.calendarType === "conflict") {
      if (evt.responseStatus === "declined" || evt.responseStatus === "needsAction") {
        continue; // declined/unresponded conflicts shouldn't render
      }
      kind = "conflict";
    } else if (link?.calendarType === "free") {
      kind = link.defaultVoteLevel === "PREFERRED" ? "preferred-align" : "feasible-align";
    } else {
      continue; // unknown calendar type — skip rather than guess
    }
    const eventId = evt.id || `${evt.calendarId}:${evt.eventId}`;
    const record = {
      id: `event:${eventId}`,
      kind,
      title: "",
      provider: evt.provider || "google",
      calendarId: evt.calendarId || null,
      eventId: evt.eventId || null,
      startUtc: start.toISOString(),
      endUtc: end.toISOString(),
      allDay: !!evt.allDay,
      tzid: evt.allDay ? "UTC" : null,
      deepLinkHref: `/availability?event=${encodeURIComponent(eventId)}`,
    };
    const finalized = finalizeAllDayBounds(record);
    if (finalized) out.push(finalized);
  }

  // 2. Marks — expand recurrence + all-day-per-day via the canonical helper.
  const markEvents = expandMarksToCalendarEvents(
    availabilityMarks || [],
    window.start,
    window.end,
  );
  for (const me of markEvents) {
    const r = me.resource || {};
    const level = r.level;
    const kind = level === "PREFERRED"
      ? "preferred-align"
      : level === "FEASIBLE"
        ? "feasible-align"
        : null;
    if (!kind) continue;
    const record = {
      id: me.id, // already shaped "mark:<markId>:<iso>" by the helper
      kind,
      title: me.title || (kind === "preferred-align" ? "Preferred" : "Feasible"),
      startUtc: me.start.toISOString(),
      endUtc: me.end.toISOString(),
      allDay: !!me.allDay,
      tzid: r.tzid || null,
      deepLinkHref: `/availability?mark=${encodeURIComponent(r.markId)}`,
    };
    const finalized = finalizeAllDayBounds(record);
    if (finalized) out.push(finalized);
  }

  // 3. Finalized-poll busy windows. Skip hidden RSVP-no sessions and the current
  // poll itself (it already renders its own slots).
  for (const sess of finalizedSessions || []) {
    if (sess.hidden) continue;
    if (sess.schedulerId === currentSchedulerId) continue;
    const start = sess.startUtc ? new Date(sess.startUtc) : null;
    const end = sess.endUtc ? new Date(sess.endUtc) : null;
    if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) continue;
    if (end.getTime() <= winStart || start.getTime() >= winEnd) continue;
    out.push({
      id: `session:${sess.schedulerId}`,
      kind: "conflict",
      title: sess.title || "Finalized session",
      startUtc: start.toISOString(),
      endUtc: end.toISOString(),
      allDay: false,
      deepLinkHref: `/scheduler/${sess.schedulerId}`,
      sourceSchedulerId: sess.schedulerId,
    });
  }

  return out;
}
